/**
 * Binance Futures Data Types
 *
 * Types for mark prices, funding rates, and futures orderbook data
 * from Binance USDⓈ-M Futures via CCXT
 */

/**
 * Mark Price Data Interface
 * Contains mark price, index price, funding rate, and best bid/ask information
 * for a futures trading pair
 */
export interface MarkPriceData {
  /** Unified symbol format (e.g., "BTCUSDT") */
  symbol: string;

  /** Current mark price used for liquidations */
  markPrice: number;

  /** Last traded price on futures market */
  lastPrice: number;

  /** Index price calculated from spot markets */
  indexPrice: number;

  /** Best bid price from book ticker stream (for opening short positions) */
  bestBid: number;

  /** Best bid quantity from book ticker stream */
  bestBidQty: number;

  /** Best ask price from book ticker stream (for opening long positions) */
  bestAsk: number;

  /** Best ask quantity from book ticker stream */
  bestAskQty: number;

  /** Current 8-hour funding rate (e.g., 0.0001 = 0.01%) */
  fundingRate: number;

  /** Unix timestamp in milliseconds for next funding time */
  nextFundingTime: number;

  /** Data timestamp in milliseconds for staleness detection */
  timestamp: number;
}

/**
 * Futures OrderBook Interface
 * Extends base orderbook with futures-specific fields
 */
export interface FuturesOrderBook {
  /** Unified symbol format (e.g., "BTC/USDT") */
  symbol: string;

  /** Bid levels: [price, volume] pairs */
  bids: [number, number][];

  /** Ask levels: [price, volume] pairs */
  asks: [number, number][];

  /** Data timestamp in milliseconds */
  timestamp: number;
}

/**
 * Staleness Detection Configuration
 */
export const STALENESS_THRESHOLD_MS = 30000; // 30 seconds

/**
 * Helper function to check if data is fresh
 * @param data - Object with timestamp field
 * @returns true if data is fresh (< 30s old)
 */
export function isFresh(data: { timestamp: number }): boolean {
  const now = Date.now();
  return (now - data.timestamp) < STALENESS_THRESHOLD_MS;
}
